import React, { Suspense, useState } from 'react';
import noop from 'lodash/noop';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Button, Modal, Text } from '@sparknz/spark-pattern-library';
import { LinkRegion } from '@sparknz/tracking';

import {
  AccountSelectorModalQuery,
  AccountSelectorModalQuery$data,
} from '__generated__/AccountSelectorModalQuery.graphql';
import {
  ChoiceBoxSelector,
  DropDownSelector,
  ModalContainer,
} from 'shared/components';
import { useSegment } from 'shared/hooks';
import { useUpdateCartAccount } from '../../hooks';
import { filterSegmentAccounts } from '../../utils/filterSegmentAccounts.utils';

const accountsQuery = graphql`
  query AccountSelectorModalQuery {
    me {
      accountList {
        accountId
        accountNumber
        firstName
        lastName
        balanceManagement
        customerSegment
        segment
      }
    }
  }
`;
interface AccountSelectorModalProps {
  isOpen: boolean;
  onUpdateAccountSuccess: (() => void) | (() => Promise<void>);
}

function AccountSelectorModalInternal({
  isOpen,
  onUpdateAccountSuccess,
}: AccountSelectorModalProps) {
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<
    string | null
  >(null);
  const [isLoading, setLoading] = useState(false);
  const { updateCartAccount } = useUpdateCartAccount();
  const { segment } = useSegment();

  const {
    me: { accountList },
  } = useLazyLoadQuery<AccountSelectorModalQuery>(
    accountsQuery,
    {},
    {
      fetchPolicy: 'store-only',
    },
  );

  const segmentedAccountList = filterSegmentAccounts({ accountList, segment });

  async function handleContinueClick() {
    setLoading(true);
    await updateCartAccount(selectedAccountNumber);
    await onUpdateAccountSuccess();
  }

  const hasMoreThanFiveAccounts = segmentedAccountList.length > 5;

  const selectorProps = {
    selectedAccountNumber,
    setSelectedAccountNumber,
    accountList: segmentedAccountList,
  };

  const joinAccountNames = (
    accounts: AccountSelectorModalQuery$data['me']['accountList'],
  ) => {
    return accounts.map((account) => {
      return {
        ...account,
        accountName: `${account.firstName} ${account.lastName}`,
      };
    });
  };

  return (
    <Modal onClose={noop} isOpen={isOpen} showCloseButton={false}>
      <ModalContainer>
        <Text variant="headingS">Select an account</Text>
        <LinkRegion isNotTracked>
          {hasMoreThanFiveAccounts ? (
            <DropDownSelector
              {...selectorProps}
              accountList={joinAccountNames(selectorProps.accountList)}
            />
          ) : (
            <ChoiceBoxSelector
              {...selectorProps}
              accountList={joinAccountNames(selectorProps.accountList)}
            />
          )}
        </LinkRegion>
        <Button
          variant="primary"
          onClick={handleContinueClick}
          isLoading={isLoading}
          disabled={!selectedAccountNumber}
        >
          Continue
        </Button>
      </ModalContainer>
    </Modal>
  );
}

export default function AccountSelector(props: AccountSelectorModalProps) {
  return (
    <Suspense fallback={null}>
      <AccountSelectorModalInternal {...props} />
    </Suspense>
  );
}
